/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    extendedStyles?: SelectPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductUnitOfMeasureSelectStyles {
    container?: InjectableCss;
    button?: InjectableCss;
}

export const productUnitOfMeasureSelectStyles: ProductUnitOfMeasureSelectStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 10px;
            margin-top: 10px;
        `,
    },
    button: {
        css: css`
            display: flex;
            width: 100%;
            gap: 10px;
            button {
                border-radius: 5px;
                &:hover {
                    background: #b00;
                }
            }
        `,
    },
};

const styles = productUnitOfMeasureSelectStyles;

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext: {
        product: { unitOfMeasures, properties },
        productInfo: { unitOfMeasure, productId },
        onUnitOfMeasureChanged,
    },
    disabled,
    productSettings,
    labelOverride,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(productUnitOfMeasureSelectStyles, extendedStyles));

    const uomChangeHandler = (event: string) => {
        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(event);
    };

    const [selectedUOM, setSelectedUOM] = useState("");

    function handleUOMChange(unitOfMeasure: any) {
        uomChangeHandler(unitOfMeasure);
        setSelectedUOM(unitOfMeasure);
    }

    const [discontinued, setDiscontinued] = React.useState(false);
    React.useEffect(() => {
        if (properties?.productDiscontinued === "true") {
            setDiscontinued(true);
        }
    }, []);

    return (
        <>
            {!discontinued && unitOfMeasures && unitOfMeasures?.length > 1 ? (
                <>
                    <StyledWrapper {...styles.container}>
                        <TypographyStyle> {labelOverride ?? translate("Pack Size")}</TypographyStyle>
                        <StyledWrapper {...styles} {...styles.button} {...otherProps}>
                            {unitOfMeasures.map(uom => {
                                const isSelected = uom.unitOfMeasure === selectedUOM;
                                const selectedStyle = {
                                    backgroundColor: "#BB0000",
                                    border: "2px solid #BB0000",
                                    color: "#fff",
                                };
                                const unselectedStyle = {
                                    backgroundColor: "#f7f7f7",
                                    border: "2px solid #444444",
                                    color: "#444444",
                                };
                                const buttonStyle = isSelected ? selectedStyle : unselectedStyle;

                                return (
                                    <Button
                                        key={uom.id.toString()}
                                        value={uom.unitOfMeasure}
                                        onClick={() => handleUOMChange(uom.unitOfMeasure)}
                                        disabled={disabled}
                                        data-test-selector="product_unitOfMeasureSelect"
                                        style={buttonStyle}
                                    >
                                        {uom.description ? uom.description : uom.unitOfMeasureDisplay}{" "}
                                        {uom.qtyPerBaseUnitOfMeasure !== 1 ? `/${uom.qtyPerBaseUnitOfMeasure}` : ""}
                                    </Button>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            ) : (
                <></>
            )}
        </>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
